// src/domain/types.ts
// Canonical TypeScript types for every Impact Ledger entity.

// ---------------------------------------------------------------------------
// Workspace + branding (spec §5)
// ---------------------------------------------------------------------------

export type TerminologyOverrides = Partial<{
  primeContractor: string;
  subcontractor: string;
  reporter: string;
  programme: string;
  project: string;
  subProject: string;
  spendAsk: string;
  /** The name the AI analyst presence goes by in this workspace (defaults to "Heimdall"). */
  analyst: string;
}>;

export type OrgLockup = { name: string; logo: string };

export type Workspace = {
  id: string;
  programmeName: string;
  funder: OrgLockup;
  recipient: OrgLockup;
  oversight: OrgLockup;
  technology: { name: "Philanthrify"; logo: string };
  accentHsl: string; // e.g. "215 75% 32%"
  /** Alpha for the accent-soft tint fill; raise it for darker/less-saturated accents so it reads. */
  accentSoftAlpha?: number;
  coverImageUrl?: string;
  compliancePackName: string;
  terminology: TerminologyOverrides;
  /** Numeric DB programme ID — used to call /api/programme/:id and /api/authority-grant. */
  programmeId?: number;
};

// ---------------------------------------------------------------------------
// Profiles (spec §6)
// ---------------------------------------------------------------------------

export type Profile = {
  id: string;
  displayName: string;
  role: string;
  organisation: string;
  avatarInitials: string;
  landing: string;
  permissions: string[];
  description?: string;
  group: "oversight" | "funder" | "contractor" | "reporter" | "onboarding" | "other";
  /** Person id in the seed this profile acts as (for audit attribution). */
  personId: string;
  /** Organisation id this profile belongs to. */
  organisationId: string;
};

// ---------------------------------------------------------------------------
// Governance memberships (demo mirror of the backend `projectmembership` table)
// ---------------------------------------------------------------------------

/** Cascade role names from the zeus-cascade role pack (spec §1 tiers). */
export type CascadeRole =
  | "stratus_admin"
  | "funder_approver"
  | "prime_lead"
  | "prime_member"
  | "sub_lead"
  | "sub_member"
  | "reporter";

/**
 * A person's role on a project — the demo-mode twin of the backend
 * `projectmembership` row (userid, projectid, role, scope).
 * No `projectId` means the role is programme-wide (oversight, funder).
 * A `subProjectId` narrows the role to that sub-project (sub_lead scope).
 */
export type DemoMembership = {
  personId: string;
  role: CascadeRole;
  projectId?: string;
  subProjectId?: string;
};

// ---------------------------------------------------------------------------
// Core hierarchy (spec §26.1)
// ---------------------------------------------------------------------------

/** Where a programme sits in its lifecycle (pilot-readiness addendum §2). */
export type ProgrammePhase = "feasibility" | "pilot" | "full" | "complete";

/** Whether the funder has been granted access to a programme yet (staged visibility). */
export type FunderVisibility = "not_shared" | "shared";

export type Programme = {
  id: string;
  workspaceId: string;
  name: string;
  funderId: string;
  recipientId: string;
  oversightId: string;
  compliancePackId: string;
  totalBudget: number;
  currency: "UGX";
  startDate: string;
  endDate: string;
  status: "active" | "on_hold" | "closed";
  alertState: "GREEN" | "AMBER" | "RED";
  /** Lifecycle phase: feasibility → pilot → full programme → complete. */
  phase?: ProgrammePhase;
  /** Staged funder access: a programme is prepared by the prime/oversight, then shared. */
  funderVisibility?: FunderVisibility;
};

export type Project = {
  id: string;
  programmeId: string;
  name: string;
  region: string;
  district: string;
  budgeted: number;
  spent: number;
  evidenced: number;
  redundancy: number;
  leadContractorId: string;
  /** Raw numeric organisation.id, distinct from leadContractorId (which
   * prefers the org's slug for org-index lookups). Real-mode identity
   * comparisons against profile.organisationId (also numeric) must use
   * this field, not leadContractorId — see the 7 call sites in hooks.ts
   * that compare against profile.organisationId. Undefined in demo mode,
   * where leadContractorId already is the correct identity value. */
  leadContractorOrgId?: string;
  // ZEUS-135: widened to include the real ZEUS governance statuses
  // (draft/review/published, set via /api/zeus-project — see ZEUS-127) on
  // top of the original demo-era values. mapApiProject already force-casts
  // whatever the API returns into this type regardless of value, so this
  // is a truthful correction, not a new risk. Existing components that
  // switch only on "active"/"planned" are unaffected by adding new members
  // (not touched here — a real governance-status project falling through to
  // their "Planned" branch is a pre-existing display gap, not something this
  // ticket's scope covers; flagged for a follow-up).
  status: "active" | "planned" | "closed" | "draft" | "review" | "published";
  procurementLineRef?: string;
  description?: string;
  coverImageUrl?: string;
  /** Schedule drift in days vs plan (positive = behind). */
  scheduleDriftDays?: number;
  /** Current build phase label (e.g. "Structural frame"). */
  currentPhase?: string;
  /** Projected end-of-quarter drift in days, for predictive analytics. */
  projectedDriftDays?: number;
  /** Geographic location of the physical site (for the map). */
  lat?: number;
  lng?: number;
  /** Site-specific "what makes this distinctive" prose, where a real one is held.
   *  Absent for sites without a captured narrative — the page falls back to derived facts. */
  siteNarrative?: string;
  /** Beneficiaries-and-impact prose, rendered only where a real site-specific one exists. */
  beneficiariesNarrative?: string;
};

export type SubProject = {
  id: string;
  projectId: string;
  name: string;
  leadContractorId: string;
  /** See Project.leadContractorOrgId — same purpose, same caveat. */
  leadContractorOrgId?: string;
  budgeted: number;
  spent: number;
  evidenced: number;
  // ZEUS-135: widened to include the real ZEUS governance statuses
  // (draft/review/published, set via /api/zeus-project — see ZEUS-127) on
  // top of the original demo-era values. mapApiProject already force-casts
  // whatever the API returns into this type regardless of value, so this
  // is a truthful correction, not a new risk. Existing components that
  // switch only on "active"/"planned" are unaffected by adding new members
  // (not touched here — a real governance-status project falling through to
  // their "Planned" branch is a pre-existing display gap, not something this
  // ticket's scope covers; flagged for a follow-up).
  status: "active" | "planned" | "closed" | "draft" | "review" | "published";
};

export type MilestoneStatus =
  | "planned"
  | "in_progress"
  | "evidence_submitted"
  | "ready_for_disbursement"
  | "disbursed"
  | "closed";

export type Milestone = {
  id: string;
  projectId: string;
  subProjectId?: string;
  name: string;
  targetDate: string;
  status: MilestoneStatus;
  budgetSharePct: number; // 0..1
  description?: string;
  evidencePackId?: string;
  disbursementId?: string;
  current?: boolean;
  /** Sequence within the project's delivery timeline; the next one unlocks on disburse. */
  order?: number;
  /** What evidence the disbursement gate requires (v3 §Chunk 11). */
  evidenceRequirements?: string[];
  /** Planned budget for this milestone (pilot-readiness addendum §3). Falls back to budgetSharePct × project budget. */
  plannedBudget?: number;
  /** Times the plan was revised after approval (light versioning). */
  planRevisions?: number;
};

// ---------------------------------------------------------------------------
// Money: currency + the spend-category taxonomy
// ---------------------------------------------------------------------------

export type Currency = "UGX" | "EUR" | "GBP" | "USD";

/** A typed amount. The canonical ledger base unit is UGX; figures convert for display. */
export type Money = { amount: number; currency: Currency };

/** Fixed mock FX table, expressed as "1 unit of key = N UGX". */
export const FX_TO_UGX: Record<Currency, number> = {
  UGX: 1,
  EUR: 4_050,
  GBP: 4_720,
  USD: 3_750,
};

/** The fixed construction-and-delivery spend taxonomy (spec §2.5). */
export const SPEND_CATEGORIES = [
  "Civil works",
  "Materials",
  "Labour",
  "Equipment and plant",
  "Logistics and transport",
  "Professional fees",
  "Health and safety",
  "Contingency",
] as const;
export type SpendCategory = (typeof SPEND_CATEGORIES)[number];

/**
 * How a spend was settled (pilot-readiness addendum §4). Traceable rails are the default;
 * cash is visible, carries stricter evidence, and is the thing the platform works to shrink.
 */
export const PAYMENT_METHODS = [
  { key: "momo_mtn", label: "Mobile money (MTN)", traceable: true },
  { key: "momo_airtel", label: "Mobile money (Airtel)", traceable: true },
  { key: "bank", label: "Bank transfer", traceable: true },
  { key: "card", label: "Card", traceable: true },
  { key: "cash", label: "Cash", traceable: false },
] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number]["key"];

/** Compliance posture of a spend ask against the active per-deal pack (spec §2.7). */
export type ComplianceState =
  | "not_required"
  | "pending_declaration"
  | "declared"
  | "cleared";

/**
 * How trustworthy a figure is (v3 §Chunk 2), in ascending order. Derived from the facts:
 * verified > matched to a statement/feed > backed by a document/receipt > only self-reported.
 */
export type AssuranceLevel =
  | "self_reported"
  | "document_backed"
  | "statement_matched"
  | "verified";

/** An explanation attached to a figure where evidence is missing or reconciliation does not match. */
export type GapExplanation = { body: string; authorId: string; createdAt: string };

/** Oversight materiality rules (v3 §Chunk 5) — the thresholds that turn data into exceptions. */
export type MaterialityConfig = {
  /** A single spend at or over this needs oversight review. */
  spendReview: number;
  /** Self-reported, money-out spend at or over this is an exception. */
  gapValue: number;
  /** Schedule drift beyond this many days is an exception. */
  driftDays: number;
  /** Project budget consumed at or over this ratio (0..1) is an exception. */
  budgetUsed: number;
};

export const DEFAULT_MATERIALITY: MaterialityConfig = {
  spendReview: 40_000_000,
  gapValue: 5_000_000,
  driftDays: 5,
  budgetUsed: 0.9,
};

// ---------------------------------------------------------------------------
// Cascading delegated authority (spec v3 §Chunk 1)
// Authority + budget cascade down the chain: each holder receives an envelope and a
// self-authorisation threshold from above, and delegates envelopes + thresholds below.
// ---------------------------------------------------------------------------

export type AuthorityGrant = {
  id: string;
  /** The person delegating the authority (the approver one level up). */
  granterId: string;
  /** The person who receives it (may self-authorise up to the threshold). */
  granteeId: string;
  /** The grantee's organisation. */
  granteeOrgId: string;
  /** What the authority is scoped to. */
  scope: { projectId?: string; subProjectId?: string; label?: string; zone?: string };
  /** Budget envelope granted (UGX base). */
  envelope: number;
  /** At or below this, the grantee records-and-evidences directly; above, they request authorisation up the chain. */
  selfAuthThreshold: number;
  status: "active" | "suspended";
  note?: string;
};

// ---------------------------------------------------------------------------
// Connections (v3 §Chunk 3) — ingest from the tools orgs already use, don't re-key.
// ---------------------------------------------------------------------------

export type ConnectionKind =
  | "mobile_money"
  | "bank"
  | "accounting"
  | "invoice_inbox"
  | "kyc"
  | "gov_records";

export type Connection = {
  id: string;
  organisationId: string;
  kind: ConnectionKind;
  provider: string; // e.g. "MTN MoMo", "QuickBooks", "Smile ID"
  status: "connected" | "not_connected" | "error";
  accountRef?: string;
  connectedAt?: string;
  lastSyncAt?: string;
  note?: string;
};

/** A transaction pulled from a connected money feed, awaiting reconciliation into spend. */
export type FeedTransaction = {
  id: string;
  connectionId: string;
  organisationId: string;
  date: string;
  description: string;
  amount: number; // UGX base
  direction: "out" | "in";
  counterparty?: string;
  suggestedCategory?: SpendCategory;
  suggestedProjectId?: string;
  suggestedSubProjectId?: string;
  status: "unreconciled" | "reconciled";
  reconciledToAskId?: string;
};

// ---------------------------------------------------------------------------
// Money flow
// ---------------------------------------------------------------------------

export type SpendState =
  | "proposed"
  | "approved_for_spend"
  | "committed"
  | "evidence_submitted"
  | "confirmed"
  | "rejected"
  | "flagged";

export type AttachedDocument = {
  id: string;
  filename: string;
  kind: "pdf" | "image" | "doc" | "xlsx";
  sizeBytes: number;
  uploadedAt: string;
  note?: string;
  uploadedById: string;
  // A durable data URL for the file, so it stays viewable after upload. Optional:
  // seed documents carry only metadata (no bytes) and render as static chips.
  url?: string;
};

export type SpendRequest = {
  id: string;
  reporterId: string;
  organisationId: string;
  projectId: string;
  subProjectId?: string;
  milestoneId?: string;
  title: string;
  amount: number;
  currency: "UGX";
  /** Budget category from the fixed taxonomy (spec §2.5). */
  category?: SpendCategory;
  /** How the spend was (or will be) settled. Traceable rails are the default; cash is stricter. */
  paymentMethod?: PaymentMethod;
  /** Reason given where cash was used (pilot-readiness addendum §4.1). */
  cashReason?: string;
  supplier?: string;
  purpose: string;
  documents: AttachedDocument[];
  state: SpendState;
  submittedAt: string;
  /** Which capture mode produced this: recorded directly (under authority) or sent for pre-authorisation. */
  path?: "pre_auth" | "recorded";
  reviewerId?: string;
  reviewedAt?: string;
  reviewerComment?: string;
  rejectionReason?: string;
  thresholdFlag?: { rule: string; routedTo: "stratus" | "funder" };
  /** Compliance posture against the active deal pack. */
  complianceState?: ComplianceState;
  /** Evidence items attached at the ask level. */
  evidenceIds?: string[];
  // --- Assurance (v3 §Chunk 2) ---
  /** Set when reconciled against a bank/mobile-money statement or feed. */
  statementMatchRef?: string;
  /** Set when independently verified by oversight (sampling / site visit). */
  verifiedById?: string;
  verifiedAt?: string;
  /** Accountable party's explanation where evidence is missing or a figure does not reconcile. */
  gapExplanation?: GapExplanation;
  /** Note recorded at independent verification (site visit / document check). */
  verificationNote?: string;
  /** Query / dispute thread (v3 §Chunk 11). */
  thread?: { id: string; authorId: string; body: string; at: string; kind: "query" | "comment" }[];
  /** Heimdall's automatic review state for this ask (advisory; never gates the lifecycle). */
  aiReview?: AIReview;
};

export type SpendItem = {
  id: string;
  spendRequestId: string;
  description: string;
  amount: number;
  currency: "UGX";
  category?: SpendCategory;
  supplier?: string;
  note?: string;
  evidence: AttachedDocument[];
  /** First-class evidence items attached to this specific line item. */
  evidenceIds?: string[];
  geoTag?: { lat: number; lng: number; capturedAt: string };
  committedAt: string;
};

// ---------------------------------------------------------------------------
// Evidence (first-class; spec §2.9)
// ---------------------------------------------------------------------------

export type EvidenceItem = {
  id: string;
  type: "photo" | "receipt" | "document";
  url: string;
  filename?: string;
  note?: string;
  geo?: { lat: number; lng: number };
  capturedAt: string;
  submittedById: string;
  linkedAskId: string;
  linkedItemId?: string;
  /** Heimdall's automatic review state for this evidence item (advisory). */
  aiReview?: AIReview;
};

// ---------------------------------------------------------------------------
// Organisations + identities
// ---------------------------------------------------------------------------

export type Organisation = {
  id: string;
  name: string;
  tier: "prime" | "sub" | "funder" | "oversight" | "platform";
  kycState: "not_submitted" | "pending_review" | "queried" | "approved" | "rejected";
  bankVerificationState:
    | "not_started"
    | "open_banking_verified"
    | "manual_submitted"
    | "mobile_money_verified";
  primaryContactPersonId: string;
  status: "active" | "suspended" | "revoked";
  /** Kanban stage for the contractors pipeline. */
  pipelineStage?:
    | "awaiting_invite"
    | "invited"
    | "in_progress"
    | "awaiting_gate"
    | "active"
    | "suspended";
  daysInStage?: number;
  assignedProgrammeId?: string;
};

export type Person = {
  id: string;
  fullName: string;
  organisationId: string;
  role: string;
  email: string;
  phone?: string;
  avatarInitials: string;
};

export type Comment = {
  id: string;
  authorId: string;
  body: string;
  createdAt: string;
  visibility: "internal" | "shared_with_contractor" | "shared_with_funder";
};

// ---------------------------------------------------------------------------
// Reviews + approvals
// ---------------------------------------------------------------------------

export type KycReview = {
  id: string;
  organisationId: string;
  programmeId: string;
  tier: "prime" | "sub";
  submittedAt: string;
  state: "pending_review" | "queried" | "approved" | "rejected";
  reviewerId?: string;
  reviewedAt?: string;
  requiredDocuments: {
    key: string;
    label: string;
    required: boolean;
    documentId?: string;
    accepted: boolean;
  }[];
  reviewerComments: Comment[];
  contractorComments: Comment[];
  structuredFields?: { label: string; value: string }[];
};

// ---------------------------------------------------------------------------
// Leaks
// ---------------------------------------------------------------------------

export type LeakCategory =
  | "timing_variance"
  | "anomalous_supplier"
  | "missing_evidence"
  | "threshold_split"
  | "suspected_duplicate";

export type Leak = {
  id: string;
  category: LeakCategory;
  title: string;
  body: string;
  amount?: number;
  projectId: string;
  contributingItemIds: string[];
  state: "open" | "investigating" | "resolved" | "not_a_leak";
  detectedAt: string;
  assignedToId?: string;
  triggeredRule?: string;
};

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export type AuditEvent = {
  id: string;
  actorId: string;
  actorOrganisationId: string;
  /**
   * Actor name/organisation as resolved by the backend (GET /api/audit).
   * Optional because seed events don't carry them: in demo mode the actor is a
   * seed person and the people/org index resolves the name, but a real user's
   * numeric id isn't in that index, so off demo mode the trail would otherwise
   * render bare ids. AuditTrail.tsx prefers the index, then these.
   */
  actorName?: string;
  actorOrganisationName?: string;
  class: "approval" | "spend" | "evidence" | "onboarding" | "kyc" | "status_change" | "system";
  description: string;
  /** Optional money figure tied to the event, rendered as a badge in the activity feed. */
  amount?: number;
  timestamp: string;
  scope: { programmeId?: string; projectId?: string; subProjectId?: string };
  /**
   * Optional id of the entity this event is about (a spend ask, milestone or disbursement).
   * Lets a trace match its activity precisely instead of by brittle description matching;
   * scope only carries programme/project/sub-project ids.
   */
  refId?: string;
};

// ---------------------------------------------------------------------------
// Invitations
// ---------------------------------------------------------------------------

export type SecureInviteLink = {
  id: string;
  token: string;
  tier: "prime" | "sub" | "staff" | "reporter";
  organisationId?: string;
  programmeId: string;
  projectScopeIds: string[];
  expiresAt: string;
  status: "pending" | "redeemed" | "expired" | "revoked" | "forwarded";
  sentAt: string;
  redeemedAt?: string;
  forwardedFromInviteId?: string;
  recipientEmail?: string;
};

// ---------------------------------------------------------------------------
// Disbursements (funder side)
// ---------------------------------------------------------------------------

export type Disbursement = {
  id: string;
  milestoneId: string;
  amount: number;
  state: "queued_for_stratus" | "ready_for_funder" | "approved" | "completed" | "queried" | "held";
  stratusRecommendation?: string;
  funderApproverId?: string;
  approvedAt?: string;
  bankingTransactionRef?: string;
  /** Confirmed spend asks that justify releasing this tranche (spec §2.1, §2.11). */
  basisAskIds?: string[];
  /** Person id of the oversight reviewer who routed it. */
  routedById?: string;
  routedAt?: string;
  /** Note recorded when a funder queries or holds the disbursement. */
  funderNote?: string;
  /** Recorded sign-offs (v3 §Chunk 7). Large tranches need two before release (multi-signature). */
  approvals?: { approverId: string; at: string }[];
};

/** Milestone tranches at or over this need two funder sign-offs before release (multi-signature). */
export const MULTISIG_THRESHOLD = 500_000_000;

// ---------------------------------------------------------------------------
// Storytelling layer
// ---------------------------------------------------------------------------

export type Notification = {
  id: string;
  kind: "approval" | "evidence" | "leak" | "milestone" | "onboarding" | "system";
  title: string;
  body: string;
  timestamp: string;
  read: boolean;
  workspaceId?: string;
  href?: string;
};

export type Briefing = {
  whereWeAre: string;
  whatIsNext: string;
  watchlist: string;
};

export type AnalyticsSeed = {
  milestones: {
    name: string;
    plannedDate: string;
    projectedDate: string;
    onTrack: boolean;
  }[];
  budget: { consumedPct: number; timeElapsedPct: number; burnRateVsPlanPct: number };
  costDrivers: {
    overrunPct: number;
    attributions: { factor: string; contributionPct: number; source: string }[];
  };
  projection: { projectedDateDriftDays: number; projectedOverBudgetPct: number };
  attentionList: {
    id: string;
    /** Drives the rail's severity soft-circle + reason chip; defaults to "info". */
    severity?: "overdue" | "due_soon" | "watch" | "info";
    kind: string;
    body: string;
    figure: string;
    href: string;
  }[];
  benchmark: {
    medianPaceVsPlanPct: number;
    medianBudgetOverrunPct: number;
    medianMilestoneDriftDays: number;
  };
};

// ---------------------------------------------------------------------------
// Reports (spec §5.1)
// ---------------------------------------------------------------------------

export type ReportType = "Impact" | "Update" | "Progress" | "Financial";
export type ReportScope = "portfolio" | "programme" | "project";
export type AudienceRole = "oversight" | "funder" | "contractor";

export type ReportSection = { heading: string; body: string };

export type Report = {
  id: string;
  title: string;
  type: ReportType;
  scope: ReportScope;
  programmeId?: string;
  projectId?: string;
  audience: string;
  periodLabel: string;
  periodStart: string;
  periodEnd: string;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  coverImageUrl?: string;
  /** Marks a report composed from thin seed data. */
  limitedData?: boolean;
  notes?: string;
  sections: ReportSection[];
  /** Which roles can see this report in the gallery. */
  visibleTo: AudienceRole[];
  /** What kind of report this is. Visit reports and Heimdall drafts are flagged for the gallery. */
  kind?: "standard" | "visit" | "progress";
  /** True when Heimdall composed the draft (always reviewed by a person before filing). */
  draftedByAI?: boolean;
  /** The person who reviewed and approved a Heimdall-drafted report. */
  reviewedById?: string;
  /** For visit reports: the site visit this report was generated from. */
  siteVisitId?: string;
};

// ---------------------------------------------------------------------------
// Heimdall — the AI analyst layer (Heimdall brief)
// A simulated analyst presence woven through the loop: it reviews every spend ask
// and evidence pack, raises findings cited to the underlying evidence, and drafts
// reports for human review. It is advisory only — a person always gates the outcome.
// ---------------------------------------------------------------------------

export type AIFindingSeverity = "info" | "attention" | "serious";

/** The integrity / quality dimensions Heimdall checks (Heimdall brief §2). */
export type AIFindingCategory =
  | "integrity"
  | "plausibility"
  | "completeness"
  | "compliance"
  | "progress";

export type AIFindingStatus = "open" | "accepted" | "dismissed";
export type AIConfidence = "high" | "moderate";

/** What a finding points at: the specific evidence, asks or visit captures that produced it. */
export type AICitations = {
  evidenceIds?: string[];
  askIds?: string[];
  captureIds?: string[];
  /** Free-text references where the cited object is not a first-class entity (e.g. a KYC review). */
  refs?: string[];
};

export type AIFinding = {
  id: string;
  severity: AIFindingSeverity;
  category: AIFindingCategory;
  /** One or two sentences, plain English: what Heimdall found. */
  statement: string;
  citations: AICitations;
  confidence: AIConfidence;
  status: AIFindingStatus;
  /** Required when status is "dismissed". */
  dismissReason?: string;
  /** The person who accepted or dismissed it. */
  decidedById?: string;
  decidedAt?: string;
  /** What the finding is attached to. */
  subjectAskId?: string;
  subjectEvidenceId?: string;
  subjectVisitId?: string;
  createdAt: string;
};

/** Heimdall's review state for an ask or an evidence item. Advisory; never changes lifecycle state. */
export type AIReviewState = "pending" | "passed" | "flagged";

export type AIReview = {
  state: AIReviewState;
  findingIds: string[];
  reviewedAt?: string;
};

// --- Site visits (Heimdall brief §5) — structured oversight capture in the field ---

/**
 * A question in the oversight-managed, programme-wide site-visit checklist template.
 * Oversight edits this template in settings; each new visit snapshots the current
 * template into its own `checklist`, so template edits never rewrite existing visits.
 * `compulsory` questions are the locked universal core and cannot be edited or removed;
 * everything else is a custom question oversight added (capped — see MAX_CUSTOM_QUESTIONS).
 */
export type SiteVisitQuestion = {
  id: string;
  label: string;
  compulsory?: boolean;
};

/** Oversight may add at most this many custom questions on top of the compulsory core. */
export const MAX_CUSTOM_QUESTIONS = 20;

/**
 * The locked universal core, defined in code so it is ALWAYS present regardless of
 * seed state — oversight cannot edit or remove these, and adding custom questions
 * never displaces them. `siteVisitQuestions` on the seed holds only the custom
 * questions; the effective template is this core followed by those customs.
 */
export const COMPULSORY_SITE_VISIT_QUESTIONS: SiteVisitQuestion[] = [
  { id: "svq-core-1", label: "Work matches the ledger claim", compulsory: true },
  { id: "svq-core-2", label: "Materials on site", compulsory: true },
  { id: "svq-core-3", label: "Site safety", compulsory: true },
];

export type VisitJudgement = "as_claimed" | "partial" | "not_as_claimed" | "unverified";
export type SiteVisitStatus = "planned" | "in_progress" | "complete";

export type VisitCapture = {
  id: string;
  type: "photo" | "note";
  /** Mock geo-tag + timestamp, as a phone would attach. */
  geo?: { lat: number; lng: number };
  takenAt: string;
  url?: string;
  note?: string;
  linkedChecklistItemId?: string;
};

export type VisitChecklistItem = {
  id: string;
  label: string;
  judgement: VisitJudgement;
  captureIds: string[];
};

export type SiteVisit = {
  id: string;
  projectId: string;
  /** The milestone the checklist was derived from. */
  milestoneId?: string;
  visitorId: string;
  plannedFor?: string;
  startedAt?: string;
  completedAt?: string;
  status: SiteVisitStatus;
  checklist: VisitChecklistItem[];
  captures: VisitCapture[];
  summary?: string;
  /** Findings raised from the visit (discrepancies attach to the relevant ask). */
  findingIds?: string[];
  reportId?: string;
};

// ---------------------------------------------------------------------------
// Sovereignty Bill compliance (pilot-readiness addendum §5)
// Compliance produces documents and declarations, not only banners. An inbound-funds
// declaration is generated from a disbursement, reviewed by oversight, and filed.
// ---------------------------------------------------------------------------

export type Declaration = {
  id: string;
  programmeId: string;
  disbursementId?: string;
  milestoneId?: string;
  title: string;
  /** The entity receiving the inbound funds. */
  receivingEntity: string;
  purpose: string;
  amount: number; // UGX base
  date: string;
  /** The authority the declaration is addressed to (e.g. Ministry of Finance). */
  authority: string;
  status: "draft" | "filed";
  generatedAt: string;
  filedAt?: string;
  filedById?: string;
};

// ---------------------------------------------------------------------------
// The per-workspace seed bundle
// ---------------------------------------------------------------------------

export type WorkspaceSeed = {
  workspace: Workspace;
  programme: Programme;
  projects: Project[];
  subProjects: SubProject[];
  milestones: Milestone[];
  organisations: Organisation[];
  people: Person[];
  /**
   * Governance memberships (person → project → cascade role). Optional: a
   * workspace without them keeps the pre-governance behaviour (org-based
   * visibility, ungated approvals) — used by the ZEUS fire-stations demo.
   */
  memberships?: DemoMembership[];
  spendRequests: SpendRequest[];
  spendItems: SpendItem[];
  authorityGrants: AuthorityGrant[];
  connections: Connection[];
  feedTransactions: FeedTransaction[];
  evidence: EvidenceItem[];
  reports: Report[];
  aiFindings: AIFinding[];
  siteVisits: SiteVisit[];
  /**
   * Oversight's CUSTOM site-visit questions (programme-wide). The compulsory core
   * lives in code (COMPULSORY_SITE_VISIT_QUESTIONS) and is always prepended, so this
   * may be empty/absent without losing the core.
   */
  siteVisitQuestions?: SiteVisitQuestion[];
  declarations: Declaration[];
  kycReviews: KycReview[];
  leaks: Leak[];
  auditEvents: AuditEvent[];
  inviteLinks: SecureInviteLink[];
  disbursements: Disbursement[];
  analytics: AnalyticsSeed;
  briefing: Briefing;
  attentionList: {
    id: string;
    /** Drives the rail's severity soft-circle + reason chip; defaults to "info". */
    severity?: "overdue" | "due_soon" | "watch" | "info";
    kind: string;
    body: string;
    figure: string;
    href: string;
  }[];
  notifications: Notification[];
  /**
   * ZEUS-129/138 demo-mode mirror of the real `projectcreationrequest` table —
   * a contractor/subcontractor's request to create a project, awaiting
   * stratus_admin review. Optional: a workspace without any behaves like the
   * pre-ZEUS-138 demo (no requests to review), same convention as
   * `memberships`/`siteVisitQuestions` above.
   */
  projectCreationRequests?: DemoProjectCreationRequest[];
};

export type DemoProjectCreationRequestStatus = "pending" | "accepted" | "rejected";

export type DemoProjectCreationRequest = {
  id: string;
  requesterActorId: string;
  /** The requester's organisation — stamped onto the created project/sub-project's
   * leadContractorId on accept, so the requesting contractor can actually see it in
   * useVisibleProjects() (see createZeusProject's requestId handling in mutations.ts). */
  requesterOrganisationId: string;
  requesterRole: string;
  proposedName: string;
  proposedDescription: string;
  justification: string;
  parentProjectId?: string;
  status: DemoProjectCreationRequestStatus;
  rejectionReason?: string;
  createdProjectId?: string;
  createdAt: string;
};
